// Lightweight client-side performance instrumentation (browser-only).
//
// Built on top of `logger` so perf marks share the same structured-JSON-line
// format, level, and error-sink plumbing as the rest of the frontend (see
// golden-principles.md). `performance` is browser-only, so the timing helpers
// degrade to a no-op duration when it is unavailable (SSR / RSC).
//
//   mark(name, fields?)              one-off structured perf mark
//   startTimer(name, fields?).stop() scoped timer; emits duration_ms
//   markComponentMount(component)    convenience wrapper for mount timing

import { logger, type LogFields } from "@/lib/logger";

const hasPerf = typeof performance !== "undefined";

/** Emit a structured perf mark via `logger.info`. */
export function mark(name: string, fields?: LogFields): void {
  logger.info(name, fields);
}

export interface PerfTimer {
  /** Stop the timer and emit the mark with `duration_ms`. Returns elapsed ms. */
  stop: (extra?: LogFields) => number;
}

/** Start a named timer. Call `.stop()` to emit the mark with `duration_ms`. */
export function startTimer(name: string, fields?: LogFields): PerfTimer {
  const start = hasPerf ? performance.now() : 0;
  return {
    stop(extra?: LogFields) {
      const duration_ms = Math.round((hasPerf ? performance.now() : 0) - start);
      logger.info(name, { ...fields, ...extra, duration_ms });
      return duration_ms;
    },
  };
}

/** Convenience: emit a `component.mount` mark with ms since navigation start. */
export function markComponentMount(component: string): void {
  if (!hasPerf) return;
  logger.info("component.mount", { component, duration_ms: Math.round(performance.now()) });
}
